import {
  BoundNumberField,
  boundSelectField,
  boundTextAreaField,
  BoundTextField,
  boundTextField,
  Button,
  Css,
  FormPageLayout,
  Icon,
  IconButton,
  MultiSelectField,
  Palette,
  Switch,
  TestIds,
  ToggleChipGroup,
  Tooltip,
  useComputed,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { fragment, Fragment, ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import {
  InputMaybe,
  ReadyPlanOptionGroupPersonalizationType,
  SavePersonalizationOptionGroupInput,
  SavePersonalizationPackageOptionInput,
  SavePersonalizationProductOptionInput,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PackageDisplayOptionField } from "src/routes/libraries/design-catalog/components/PackageDisplayOptionField";
import {
  MaterialSelectionResult,
  PlaceholderMaterialSelection,
  StagedProductSearchModal,
} from "src/routes/libraries/design-catalog/components/PlaceholderProductSearch";
import {
  EditPersonalizationOptionPage_MaterialVariantFragment,
  EditPersonalizationOptionPage_OptionCombinationFragment,
  EditPersonalizationOptionPage_ReadyPlanOptionGroupFragment,
  EditPersonalizationOptionPage_Slot_TakeoffLineItemFragment,
  EditPersonalizationOptionPageQuery,
  useEditPersonalizationOptionPage_CombinationsQuery,
  useEditPersonalizationOptionPageQuery,
  useSavePersonalizationRpogMutation,
} from "src/routes/libraries/design-catalog/EditPersonalizationOptionPage.generated";
import { PRODUCT_FALLBACK_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";
import { IdOrAddParams } from "src/routes/routesDef";
import { createDesignPackageUrl, createEditPersonalizationOptionUrl } from "src/RouteUrls";
import { assertNever, fail, zeroTo } from "src/utils";
import { AddSlotModal } from "./components/AddSlotModal";

/** Creates/updates Personalization Options. */
export function EditPersonalizationOptionPage() {
  const searchParams = new URLSearchParams(useLocation().search);
  const forTli = searchParams.get("forTli") || undefined;
  const returnTo = searchParams.get("returnTo") || undefined;
  const {
    idOrAdd: rpogId,
    designPackageId: dpId,
    designPackageVersionId: rpavId,
  } = useRouteParams<IdOrAddParams & { designPackageId: string; designPackageVersionId: string }>();
  const { triggerNotice } = useSnackbar();
  const navigate = useNavigate();
  const isAdd = rpogId === "add";

  const query = useEditPersonalizationOptionPageQuery({
    variables: {
      dpId,
      rpavId,
      rpogId,
      isAdd, // We'll @skip querying for the rpogId when `isAdd` is true
      forTliId: forTli ?? "",
      hasTliId: !!forTli, // We use `forTli` in both new-option & add-to-option flows
    },
  });
  const tid = useTestIds({});
  const [mutate] = useSavePersonalizationRpogMutation();
  const formState = useFormState({
    config: formConfig,
    init: { query, map: (data) => mapToForm(data, isAdd, forTli) },
    readOnly: query.data?.designPackage.canEdit.allowed === false,
  });
  const personalizationType = useComputed(() => formState.type.value, [formState]);

  useEffect(() => {
    const tli = query.data?.takeoffLineItem;
    if (isAdd && personalizationType && tli) {
      // When adding, use the initial placeholder's products as the initial materials
      // (Before careful inside this impl to not nuke wip data, as the useEffect can easily be called twice)
      initializeOptions(personalizationType, formState, tli);
    } else if (!isAdd && tli) {
      // When add-to-option-ing, fill in the existing cards
      const rpoIdToRpogId = buildRpoIdToRpogIdMap(query.data?.designPackage.availableTargetGroups ?? []);
      addExistingMaterials(formState, tli, rpoIdToRpogId);
      // This `useEffect` can be called twice, so only add if it doesn't exist yet
      if (!formState.placeholders.value.some((p) => p.id === tli.id)) {
        formState.placeholders.set([...formState.placeholders.value, { id: tli.id, data: fragment(tli) }]);
      }
    }
  }, [formState, personalizationType, isAdd, query]);

  async function save() {
    const { placeholders, productOptions, packageOptions, ...rest } = formState.changedValue;
    const { data, errors } = await mutate({
      variables: {
        input: {
          ...(isAdd ? { designPackageId: dpId } : {}),
          ...rest,
          // This is exhaustive; any placeholder IDs that we leave out will be unhooked
          controlledSlots: formState.placeholders.value.map((tli) => ({
            placeholderId: tli.id,
            order: tli.order ?? null,
          })),
          ...(formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package
            ? packageOptions
              ? {
                  // This is exhaustive; any package options that we leave out will be unhooked
                  packageOptions: formState.packageOptions.value?.map((po) => ({
                    ...po,
                    products: po.products?.map(({ productMaterial, ...p }) => ({
                      placeholderMaterialId: p.placeholderMaterialId,
                      productMaterialId: productMaterial?.id ?? null,
                      locationId: p.locationId,
                    })),
                  })),
                }
              : {}
            : {
                productOptions: formState.productOptions.value?.map((po) => ({
                  materialVariantId: po.materialVariantId,
                  personalizationTargetCombinations: po.personalizationTargetCombinations?.map((combo) => ({
                    options: combo.options,
                  })),
                })),
              }),
        },
      },
      refetchQueries: ["DesignPackageItemSlotEditor"],
    });
    if (!data || errors?.nonEmpty) return;
    // Use the version ID from the mutation response, which may be a new draft if the save
    // created one (e.g. when editing an active version with no existing draft).
    const newRpavId = data.savePersonalizationOptionGroup.version.id;
    triggerNotice({ message: `Option ${isAdd ? "created" : "updated"} successfully`, icon: "success" });
    if (isAdd) {
      const newRpogId = data.savePersonalizationOptionGroup.optionGroup.id;
      // Update URL the newly created option, dropping forTli since it's now an existing og
      navigate(createEditPersonalizationOptionUrl(dpId, newRpavId, newRpogId, undefined, returnTo), { replace: true });
    } else if (newRpavId !== rpavId) {
      // Update URL in case the save created a new draft version
      navigate(createEditPersonalizationOptionUrl(dpId, newRpavId, rpogId, undefined, returnTo), { replace: true });
    }
  }
  const maybeRpog = query.data?.readyPlan?.optionGroups.first;
  const selectedTargetGroups = useComputed(
    () => formState.personalizationTargetGroupIds.value ?? [],
    [formState.personalizationTargetGroupIds],
  );
  // Get option combinations based on the selected target groups
  const combinationsQuery = useEditPersonalizationOptionPage_CombinationsQuery({
    variables: { dpId, rpavId, targetGroupIds: selectedTargetGroups },
    skip: selectedTargetGroups.isEmpty,
  });

  return (
    <FormPageLayout
      pageTitle={maybeRpog ? `Edit ${maybeRpog.name} Option` : "Create New Option"}
      formSections={[
        {
          title: "Details",
          rows: [
            {
              type: boundSelectField({
                options: query.data?.enumDetails.readyPlanOptionGroupPersonalizationType ?? [],
                getOptionLabel: (o) => o.name,
                getOptionValue: (o) => o.code,
                disabled: maybeRpog ? "Type cannot be modified for existing options" : undefined,
              }),
            },
            { name: boundTextField({}) },
            { marketingDescription: boundTextAreaField({}) },
            {
              levels: boundSelectField({
                options: [{ name: "", value: null }, ...zeroTo(5, (i) => ({ name: `${i + 1}`, value: i + 1 }))],
                getOptionLabel: (v) => v.name,
                getOptionValue: (v) => v.value,
              }),
            },
            ...(maybeRpog?.personalizationType?.code === ReadyPlanOptionGroupPersonalizationType.Package
              ? [
                  {
                    reactNodeDisplayPackageOptionsAsSingleProduct: (fs: ObjectState<FormValue>) => (
                      <PackageDisplayOptionField field={fs.displayPackageOptionsAsSingleProduct} />
                    ),
                  },
                ]
              : []),
          ],
        },
        {
          title: "Slots",
          rows: [
            {
              reactNodeFoo: (
                <SlotsSection
                  formState={formState}
                  tid={tid}
                  targetGroups={query.data?.designPackage.availableTargetGroups ?? []}
                />
              ),
            },
          ],
        },

        ...(!canAssignTargetCombinations(personalizationType)
          ? []
          : [
              {
                title: "Apply to",
                rows: [
                  {
                    reactNodeFoo: (
                      <TargetGroupsSection
                        formState={formState}
                        tid={tid}
                        targetGroups={query.data?.designPackage.availableTargetGroups ?? []}
                      />
                    ),
                  },
                ],
              },
            ]),
        {
          title: "Variants",
          rows: [
            {
              reactNodeSection: (
                <VariantsSection
                  tid={tid}
                  formState={formState}
                  targetCombinations={combinationsQuery.data?.designPackage.combinations ?? []}
                  targetGroups={query.data?.designPackage.availableTargetGroups ?? []}
                />
              ),
            },
          ],
        },
      ]}
      formState={formState}
      cancelAction={{ label: "Cancel", onClick: returnTo || createDesignPackageUrl(dpId, rpavId) }}
      submitAction={{ label: isAdd ? "Create" : "Save Options", onClick: save, disabled: formState.readOnly }}
    />
  );
}

type PackageOption = Pick<
  SavePersonalizationPackageOptionInput,
  "id" | "name" | "personalizationTargetCombinations"
> & {
  products: Array<{
    placeholderMaterialId: string;
    locationId?: string;
    productMaterial: { id: string; data: EditPersonalizationOptionPage_MaterialVariantFragment } | undefined;
  }>;
};

type ProductOption = Pick<
  SavePersonalizationProductOptionInput,
  "materialVariantId" | "personalizationTargetCombinations"
> & { data: Fragment<EditPersonalizationOptionPage_MaterialVariantFragment> };

type FormValue = Omit<SavePersonalizationOptionGroupInput, "packageOptions" | "productOptions" | "controlledSlots"> & {
  placeholders: Array<{
    id: string;
    data: Fragment<EditPersonalizationOptionPage_Slot_TakeoffLineItemFragment>;
    order?: number | null;
  }>;
  productOptions: Array<ProductOption>;
  packageOptions: Array<PackageOption>;
};

const formConfig: ObjectConfig<FormValue> = {
  id: { type: "value" },
  type: { type: "value", rules: [required] },
  name: { type: "value", rules: [required] },
  marketingDescription: { type: "value" },
  levels: { type: "value" },
  displayPackageOptionsAsSingleProduct: { type: "value" },
  // Ideally this could be a `type: "fragment"` that was a `TliFragment[]` but
  // currently fragmentField too naively snapshots its value during the 1st init.map
  // run (when we have no data/are loading) and never updates it to the new value.
  placeholders: {
    type: "list",
    config: { id: { type: "value" }, data: { type: "fragment" }, order: { type: "value" } },
  },
  personalizationTargetGroupIds: { type: "value" },
  productOptions: {
    type: "list",
    config: {
      materialVariantId: { type: "value" },
      personalizationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
      data: { type: "fragment" },
    },
  },
  packageOptions: {
    type: "list",
    config: {
      id: { type: "value" },
      name: { type: "value", rules: [required] },
      products: {
        type: "list",
        config: {
          placeholderMaterialId: { type: "value" },
          locationId: { type: "value" },
          productMaterial: { type: "value" },
        },
      },
      personalizationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
    },
  },
  groupByFeature: { type: "value" },
};

function mapToForm(data: EditPersonalizationOptionPageQuery, isAdd: boolean, forTli?: string) {
  if (isAdd) {
    return {
      type: ReadyPlanOptionGroupPersonalizationType.SingleProduct,
      name: "",
      marketingDescription: "",
      levels: undefined,
      // When adding, the caller should pass in the initial placeholder via `forTli`, which we seed
      placeholders: [{ id: forTli ?? fail(), data: fragment(data.takeoffLineItem ?? fail()) }],
      // Product/Package options will be added by `setMaterialVariants` after the user selects the type
      productOptions: [],
      packageOptions: [],
      personalizationTargetGroupIds: data.takeoffLineItem?.targetGroups.map((tg) => tg.id),
    };
  }
  const rpog = data.readyPlan?.optionGroups.first ?? fail("No ReadyPlanOptionGroup found");
  const {
    id,
    name,
    marketingDescription,
    personalizationType,
    personalizationPlaceholders,
    personalizationTargetGroups,
    options,
    levels,
    displayPackageOptionsAsSingleProduct,
    groupByFeature,
  } = rpog;
  const type = personalizationType?.code;
  return {
    id,
    type,
    name,
    marketingDescription,
    personalizationTargetGroupIds: personalizationTargetGroups.map((tg) => tg.id),
    levels,
    displayPackageOptionsAsSingleProduct,
    groupByFeature,
    // When editing we show our controlled slots
    placeholders:
      personalizationPlaceholders
        ?.flatMap((pp) => pp.personalizationPlaceholderControlledSlots)
        .sortByKey("name")
        .map((tli) => {
          return { id: tli.id, data: fragment(tli), order: tli.personalizationSlotOrder };
        }) ?? [],
    // Form Package types we need to initialize the package options
    ...(type === ReadyPlanOptionGroupPersonalizationType.Package
      ? {
          productOptions: [],
          packageOptions: options.map((o) => ({
            id: o.id,
            name: o.name,
            // We scan look at our controlled slots, and their RPO placeholder's product cards, to find
            // find our products, i.e. product cards with "options=[Samsung Appliances]"
            products: personalizationPlaceholders.flatMap(
              (pp) =>
                pp.personalizationRpoShadowPlaceholder?.products
                  .filter((p) => p.options.every((opt) => opt.id === o.id))
                  .compact()
                  .map((p) => ({
                    placeholderMaterialId: p.placeholder!.materialVariant!.id,
                    productMaterial: p.materialVariant
                      ? { id: p.materialVariant.id, data: p.materialVariant }
                      : undefined,
                    locationId: groupByFeature ? p.location.id : undefined,
                  })) ?? [],
            ),
            personalizationTargetCombinations: rpoCombosToRpoIds(o.personalizationTargetCombinations),
          })),
        }
      : // Form Single/Linked types we need to initialize the materials
        {
          productOptions:
            options.map((rpo) => {
              return {
                materialVariantId: rpo.materialVariant!.id,
                data: fragment(rpo.materialVariant!),
                personalizationTargetCombinations: rpoCombosToRpoIds(rpo.personalizationTargetCombinations),
              };
            }) ?? [],
          packageOptions: [],
        }),
  };
}

const SlotsSection = observer(function SlotsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetGroups: EditPersonalizationOptionPage_ReadyPlanOptionGroupFragment[];
}) {
  const { formState, tid, targetGroups } = props;
  const { openModal } = useModal();
  const { designPackageId, designPackageVersionId } = useRouteParams<{
    designPackageId: string;
    designPackageVersionId: string;
  }>();
  const isPackageType = formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package;
  // Sort placeholders by order for display (ordered slots first, then unordered)
  const sortedPlaceholders = useComputed(
    () => [...formState.placeholders.rows].sortBy((row) => row.order.value ?? Infinity),
    [formState.placeholders],
  );

  return (
    <div>
      {sortedPlaceholders.map((tli) => (
        <div key={tli.id.value} {...tid.slotLocation} css={Css.df.aic.gap2.mb1.$}>
          {isPackageType && (
            <div css={Css.wPx(80).$}>
              <BoundNumberField
                field={tli.order}
                label="Order"
                labelStyle="hidden"
                placeholder="Order"
                disabled={formState.readOnly}
                {...tid.slotOrder}
              />
            </div>
          )}
          <div css={Css.fg1.$}>
            {tli.data.value.name} - {tli.data.value.location.displayLocationPath}
          </div>
          {formState.placeholders.rows.length > 1 && (
            <IconButton
              icon="remove"
              disabled={formState.readOnly}
              onClick={() => {
                formState.placeholders.remove(tli.value);
                // For package groups, remove products that are now invalid based on the removed placeholder
                if (formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package) {
                  formState.packageOptions.rows.forEach((po) => {
                    po.products.rows
                      .filter(
                        (p) =>
                          !formState.placeholders.rows.some(
                            (pl) =>
                              pl.data.value.materialVariant?.id === p.placeholderMaterialId.value &&
                              (!formState.groupByFeature.value || pl.data.value.location.id === p.locationId.value),
                          ),
                      )
                      .forEach((p) => po.products.remove(p.value));
                  });
                }
              }}
              {...tid.slotRemove}
            />
          )}
        </div>
      ))}
      {formState.type.value ? (
        <div css={Css.mt2.$}>
          <Button
            label="Add Slots"
            variant="secondary"
            disabled={formState.readOnly}
            icon="plus"
            onClick={() =>
              openModal({
                content: (
                  <AddSlotModal
                    designPackageId={designPackageId}
                    designPackageVersionId={designPackageVersionId}
                    existingPlaceholderIds={formState.placeholders.value.map((p) => p.id)}
                    onAddSlots={(placeholders) => {
                      const rpoIdToRpogId = buildRpoIdToRpogIdMap(targetGroups);
                      placeholders.forEach((p) => {
                        formState.placeholders.add({ id: p.id, data: fragment(p) });
                        addExistingMaterials(formState, p, rpoIdToRpogId);
                      });
                    }}
                    // If we are adding slots for a product, we want to filter the slots by material variant
                    materialVariantId={maybeFilterByPlaceholder(formState)}
                  />
                ),
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
});

const TargetGroupsSection = observer(function TargetGroupsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetGroups: EditPersonalizationOptionPage_ReadyPlanOptionGroupFragment[];
}) {
  const { tid, formState, targetGroups } = props;
  return (
    <div>
      {targetGroups.nonEmpty ? (
        <ToggleChipGroup
          {...tid.targetGroupChipGroup}
          label="Target Groups"
          labelStyle="hidden"
          options={targetGroups.map((tg) => ({ label: tg.name, value: tg.id }))}
          values={formState.personalizationTargetGroupIds?.value ?? []}
          readonly={formState.readOnly}
          onChange={(tgIds) => {
            formState.personalizationTargetGroupIds.set(tgIds);
            // Clear option combinations when target groups change
            [...formState.productOptions.rows, ...formState.packageOptions.rows].forEach((row) =>
              row.personalizationTargetCombinations.set([]),
            );
          }}
        />
      ) : (
        <div css={Css.asc.sm.$}>No options available</div>
      )}
    </div>
  );
});

type VariantsSectionProps = {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetCombinations: EditPersonalizationOptionPage_OptionCombinationFragment[];
  targetGroups: EditPersonalizationOptionPage_ReadyPlanOptionGroupFragment[];
};

const VariantsSection = observer(function VariantsSection(props: VariantsSectionProps) {
  const { tid, formState, targetCombinations, targetGroups } = props;
  switch (formState.type.value) {
    case ReadyPlanOptionGroupPersonalizationType.SingleProduct:
    case ReadyPlanOptionGroupPersonalizationType.LinkedProduct:
      return <ProductVariantsSection tid={tid} formState={formState} targetCombinations={targetCombinations} />;
    case ReadyPlanOptionGroupPersonalizationType.Package:
      return (
        <PackageVariantsSection
          tid={tid}
          formState={formState}
          targetCombinations={targetCombinations}
          targetGroups={targetGroups}
        />
      );
    default:
      return null;
  }
});

/** The variants for linked/single product personalization options. */
const ProductVariantsSection = observer(function ProductVariantsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetCombinations: EditPersonalizationOptionPage_OptionCombinationFragment[];
}) {
  const { tid, formState, targetCombinations } = props;
  const { openModal } = useModal();
  // Assume we have at least 1 controlled placeholder, and we can use it as our "requirements" for the product search
  const placeholderTliId = formState.placeholders.value.first?.id;
  return (
    <div css={Css.df.fdc.gap2.$}>
      <InlineGrid /* product list */>
        {formState.productOptions.rows.map((productOption) => {
          const mv = productOption.data.value;
          return (
            <div key={mv.id} css={Css.df.fdc.wPx(208).tal.hPx(310).bshBasic.p2.$}>
              <div css={Css.df.aic.mb1.wPx(176).hPx(150).$}>
                <img
                  css={Css.m1.p1.br8.maxw100.maxh100.ma.p1.$}
                  src={mv.featuredImage?.asset?.previewUrl || PRODUCT_FALLBACK_IMG_URL}
                  alt={mv.displayName}
                />
              </div>
              <div css={Css.df.jcsb.aic.hPx(100).$}>
                <span css={Css.lineClamp4.$}>
                  {mv.listing.brand && (
                    <>
                      <label css={Css.smSb.$}>{mv.listing.brand.name}</label>{" "}
                    </>
                  )}
                  <Tooltip {...tid.materialName} title={mv.displayName} placement="top">
                    {mv.displayName}
                  </Tooltip>
                </span>
                <IconButton
                  icon="remove"
                  disabled={formState.readOnly}
                  onClick={() => formState.productOptions.remove(productOption.value)}
                  {...tid.materialRemove}
                />
              </div>
              {canAssignTargetCombinations(formState.type.value) && (
                <div css={Css.mt1.$}>
                  <MultiSelectField
                    label="Option Combinations"
                    disabled={targetCombinations.isEmpty}
                    options={targetCombinations}
                    placeholder="Select Option Combinations"
                    labelStyle="hidden"
                    onSelect={(val) => {
                      removeDuplicateCombinations(productOption, formState.productOptions.rows, val);
                      productOption.personalizationTargetCombinations.set(val.map((v) => ({ options: v.split(",") })));
                    }}
                    values={productOption.personalizationTargetCombinations.value.map((c) => c.options.join(","))}
                  />
                </div>
              )}
            </div>
          );
        })}
        {placeholderTliId && (
          <div css={Css.df.hPx(310).wPx(200).br8.bgGray100.$}>
            <div css={Css.ma.$}>
              <Button
                label="Add Variant"
                variant="secondary"
                icon="plus"
                disabled={formState.readOnly}
                onClick={() =>
                  openModal({
                    content: <ProductSearchModal placeholderTliId={placeholderTliId} formState={formState} />,
                    size: "xxl",
                  })
                }
              />
            </div>
          </div>
        )}
      </InlineGrid>
    </div>
  );
});

/** The variants for package product personalization options. */
const PackageVariantsSection = observer(function VariantsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetCombinations: EditPersonalizationOptionPage_OptionCombinationFragment[];
  targetGroups: EditPersonalizationOptionPage_ReadyPlanOptionGroupFragment[];
}) {
  const { tid, formState, targetCombinations, targetGroups } = props;
  const { openModal } = useModal();
  const groupByFeature = !!formState.groupByFeature.value;

  // Determine the columns of the Package Options:
  // - When `groupByFeature=false`, we use a column per unique placeholder variant (i.e. all 32in sinks get 1 column)
  // - When `groupByFeature=true`, we use a column per unique placeholder + location pair (i.e. Tile+Wall, Tile+Floor are separate columns)
  // Because DPs already generalizes slots per placeholder/feature pair (20 cabinet knob assembly slots generalized to Kitchen > Island),
  // for `groupByFeature=true`, we can use our placeholder's location as-is, and trust that it's a) unique, and b) already feature-or-higher.
  const placeholderColumns = groupByFeature
    ? formState.placeholders.rows
    : formState.placeholders.rows.uniqueBy((p) => p.data.value.materialVariant?.id);

  return (
    <>
      <div css={Css.df.fdc.gap2.$}>
        <Switch
          label="Group By Feature"
          labelStyle="form"
          disabled={formState.readOnly}
          selected={groupByFeature}
          onChange={(groupByFeature) => {
            formState.groupByFeature.set(groupByFeature);
            const rpoIdToRpogId = buildRpoIdToRpogIdMap(targetGroups);
            maybeGroupByFeature(formState, groupByFeature, rpoIdToRpogId);
          }}
        />
        {/* Sticky column headers for each material placeholder/location, using the css for the cards from below. */}
        <div
          // 120px is hardcoded height of header w/in beam
          css={Css.mt2.df.gap2.sticky.topPx(120).z2.bgWhite.bb.bcGray300.pb1.$}
        >
          {placeholderColumns.map((placeholder) => {
            const fragment = placeholder.data.value;
            return (
              <div key={`${fragment.materialVariant?.id}-${fragment.location.id}`} css={Css.wPx(200).fw7.$}>
                {fragment.name}
                {groupByFeature && <div>{fragment.location.displayLocationPath}</div>}
              </div>
            );
          })}
        </div>
        {formState.packageOptions.rows.map((packageOption, i) => {
          return (
            <div key={`${packageOption.id.value}-${i}`}>
              <div css={Css.df.aic.gap2.$}>
                <BoundTextField field={packageOption.name} placeholder="Add Package Option Name" labelStyle="hidden" />
                <div css={Css.maxw50.$}>
                  <MultiSelectField
                    label="Option Combinations"
                    disabled={targetCombinations.isEmpty || formState.readOnly}
                    options={targetCombinations}
                    placeholder="Select Option Combinations"
                    labelStyle="hidden"
                    onSelect={(val) => {
                      removeDuplicateCombinations(packageOption, formState.packageOptions.rows, val);
                      packageOption.personalizationTargetCombinations.set(val.map((v) => ({ options: v.split(",") })));
                    }}
                    values={packageOption.personalizationTargetCombinations.value.map((c) => c.options.join(","))}
                  />
                </div>
                <IconButton
                  icon="trash"
                  disabled={formState.readOnly}
                  onClick={() => formState.packageOptions.remove(packageOption.value)}
                  {...tid.packageOptionRemove}
                />
              </div>

              {/* TODO: right now the entire page is horizontal scrollable, because I was struggling to make each section independently scrollable */}
              <div css={Css.mt2.df.gap2.bb.bcGray600.$}>
                {placeholderColumns.map((placeholder) => {
                  const key = `${placeholder.data.value.materialVariant?.id}-${placeholder.data.value.location.id}-${packageOption.name.value}`;
                  const material = packageOption.products.rows.find((r) => {
                    const sameMv = r.placeholderMaterialId.value === placeholder.data.value.materialVariant?.id;
                    // if !groupByFeature, we need to ignore the location b/c `placeholder` is a ~randomly selected controlled
                    // regular placeholder, and our product might have a different location (or our RPOG location).
                    const sameLocation = !groupByFeature || r.locationId.value === placeholder.data.value.location.id;
                    return sameMv && sameLocation;
                  });
                  const mv = material?.productMaterial.value?.data;
                  if (material) {
                    return (
                      <div
                        {...tid.productCard}
                        key={key}
                        css={Css.mhPx(300).wPx(200).position("relative").$}
                        onClick={() =>
                          openModal({
                            content: (
                              <ProductSearchModal
                                placeholderTliId={placeholder.id.value}
                                formState={formState}
                                packageOption={packageOption}
                              />
                            ),
                            size: "xxl",
                          })
                        }
                      >
                        <div
                          css={Css.position("absolute").top1.right0.z1.$}
                          onClick={(e) => e.stopPropagation()} // Prevent triggering the parent onClick
                        >
                          <IconButton
                            icon="trash"
                            disabled={formState.readOnly}
                            {...tid.packageOptionProductRemove}
                            onClick={() => {
                              packageOption.products.remove(material.value);
                            }}
                          />
                        </div>
                        {mv ? (
                          <>
                            <img
                              css={Css.m1.p1.br8.$}
                              src={mv.featuredImage?.asset?.previewUrl || PRODUCT_FALLBACK_IMG_URL}
                              alt={mv.displayName}
                            />
                            <div css={Css.df.jcsb.aic.$}>
                              <div>
                                {mv.listing.brand && <div>{mv.listing.brand.name}</div>}
                                <div {...tid.materialName}>{mv.displayName}</div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div {...tid.materialName} css={Css.h100.df.fdc.aic.jcc.gap1.tac.ba.bcGray500.bgGray50.br4.$}>
                            <Icon icon="remove" color={Palette.Gray700} inc={4} />
                            <div css={Css.gray700.mdSb.$}>No Product</div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={key} css={Css.df.hPx(300).wPx(200).br8.bgGray100.$}>
                      <div css={Css.ma.$}>
                        <Button
                          label="Add Variant"
                          variant="secondary"
                          icon="plus"
                          onClick={() =>
                            openModal({
                              content: (
                                <ProductSearchModal
                                  placeholderTliId={placeholder.id.value}
                                  formState={formState}
                                  packageOption={packageOption}
                                />
                              ),
                              size: "xxl",
                            })
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div css={Css.mt2.$}>
        <Button
          label="Add Set"
          variant="secondary"
          icon="plus"
          disabled={formState.readOnly}
          onClick={() => formState.packageOptions.add({ products: [] })}
        />
      </div>
    </>
  );
});

/**
 * Wraps the `PlaceholderProductSearch` component in a modal.
 *
 * Unlike the existing DP "product search" flow, which is page/route based, we cannot leave the
 * page here b/c the user might have WIP changes to a new/unsaved RPOG.
 */
type ProductSearchModalProps = {
  placeholderTliId: string;
  formState: ObjectState<FormValue>;
  packageOption?: ObjectState<PackageOption>;
};

function ProductSearchModal(props: ProductSearchModalProps) {
  const { placeholderTliId, formState, packageOption } = props;
  const currentMaterials = useComputed<PlaceholderMaterialSelection>(() => {
    return { kind: "materials", ids: formState.productOptions.value.map((po) => po.materialVariantId) };
  }, [formState]);

  // Note: for the LinkedProduct/SingleProduct pool, this only ever saves the single staged pick;
  // we COULD allow staging/saving multiple products at once, but we should wait for user feedback
  // before adding that complexity.
  function saveStagedSelection(selection: MaterialSelectionResult) {
    // mv is only undefined for "noMaterialVariant" selections, which can only happen for Package options
    const mv = selection.kind === "material" ? selection.mv : undefined;
    switch (formState.type.value) {
      case ReadyPlanOptionGroupPersonalizationType.LinkedProduct:
      case ReadyPlanOptionGroupPersonalizationType.SingleProduct:
        // Single/Linked are just a single pool of products, just make sure this one doesn't already exist
        if (mv && !formState.productOptions.value.some((po) => po.materialVariantId === mv.id)) {
          formState.productOptions.add({ materialVariantId: mv.id, data: fragment(mv) });
        }
        break;
      case ReadyPlanOptionGroupPersonalizationType.Package: {
        if (!packageOption) fail("Expecting packageOption prop");
        const groupByFeature = formState.groupByFeature.value;
        // placeholderTlid will be our controlled placeholder
        const placeholder =
          formState.placeholders.rows.find((p) => p.id.value === placeholderTliId) ??
          fail(`Missing placeholder ${placeholderTliId}`);
        const productMaterial = mv ? { id: mv.id, data: mv } : undefined;
        // Package Option cells can only have a single product, so do an add if none yet, or update if existing
        const existingProduct = packageOption.products.rows.find(
          (p) =>
            p.placeholderMaterialId.value === placeholder.data.value.materialVariant?.id &&
            // If groupByFeature, our controlled placeholder will have DP already-generalized locations
            // like `Bathroom` (room) or `Bathroom / Wall` (feature) so we can use it as-is
            (groupByFeature ? p.locationId.value === placeholder.data.value.location.id : true),
        );
        if (existingProduct) {
          existingProduct.productMaterial.set(productMaterial);
        } else {
          packageOption.products.add({
            placeholderMaterialId: placeholder.data.value.materialVariant!.id,
            productMaterial,
            // Even if !groupByFeature, we'll send up our controlled placeholder's location, but
            // the backend will ignore it and use our RPOG.location
            locationId: placeholder.data.value.location.id,
          });
        }
        break;
      }
      default:
        break;
    }
  }

  return (
    <StagedProductSearchModal
      placeholderTliId={placeholderTliId}
      showNoMaterialVariant={!!packageOption}
      currentMaterials={currentMaterials}
      onSave={saveStagedSelection}
      // Single/Linked share one pool of products, so an already-assigned material should be disabled
      // rather than re-selectable; Package cells are edited by re-picking, so leave those clickable.
      disableAssigned={!packageOption}
    />
  );
}

const InlineGrid = ({ children }: React.PropsWithChildren<unknown>) => (
  <div css={Css.dg.gtc("repeat(auto-fill, 200px)").gar("340px").gap2.mb4.$}>{children}</div>
);

function rpoCombosToRpoIds(combos: { options: { id: string }[] }[]): { options: string[] }[] {
  return combos.map((combo) => ({ options: combo.options.map((o) => o.id) }));
}

/** Removes duplicate combinations from other formState rows based on the current row's selected values */
function removeDuplicateCombinations<T extends ObjectState<ProductOption> | ObjectState<PackageOption>>(
  currentRow: T,
  allRows: readonly T[],
  selectedValues: string[],
) {
  allRows.forEach((row) => {
    if (row === currentRow) return;
    const current = row.personalizationTargetCombinations.value;
    const filtered = current.filter((c) => !selectedValues.includes(c.options.join(",")));
    if (filtered.length !== current.length) {
      row.personalizationTargetCombinations.set(filtered);
    }
  });
}

/** Sorts target combinations by their options */
function sortTargetCombinations(combos: { options: string[] }[]) {
  return combos
    .map((c) => ({ options: c.options.toSorted() }))
    .toSorted((a, b) => a.options.join(",").localeCompare(b.options.join(",")));
}

/**
 * Builds a map from RPO ID to its parent RPOG ID, to be used for partial combination matching. */
function buildRpoIdToRpogIdMap(
  targetGroups: EditPersonalizationOptionPage_ReadyPlanOptionGroupFragment[],
): Map<string, string> {
  const map = new Map<string, string>();
  targetGroups.forEach((rpog) => rpog.options.forEach((rpo) => map.set(rpo.id, rpog.id)));
  return map;
}

/**
 * Checks if any of the product's combinations matches all the package option's combinations,
 * supporting partial combinations where omitted option groups = applies to all values.
 *
 * @param productCombinations - Product's target combinations (the pool to search within)
 * @param packageOptionCombinations - Package option's combinations (all must be matched)
 * @param rpoIdToRpogId - Map from RPO ID to its RPOG ID
 * @returns true if all package option combinations have a matching product combination
 */
function hasMatchingPartialCombination(
  productCombinations: { options: string[] }[],
  packageOptionCombinations: { options: string[] }[],
  rpoIdToRpogId: Map<string, string>,
): boolean {
  // All package option combinations must be matched by at least one product combination
  return (
    packageOptionCombinations.nonEmpty &&
    packageOptionCombinations.every((packageOptionCombo) => {
      const pkgRpogIds = packageOptionCombo.options.map((rpoId) => rpoIdToRpogId.get(rpoId)!).unique();
      return productCombinations.some((productCombo) => {
        // If product combo is more specific than package combo, don't match
        // e.g. package [Premium] should not match product [Bold, Premium]
        if (productCombo.options.length > packageOptionCombo.options.length) return false;
        return pkgRpogIds.every((rpogId) => {
          // We're at the RPOG level (e.g. SpecLevel), see what RPO (essential/deluxe/premium) each package/product combo is using
          const pkgRpoForRpog = packageOptionCombo.options.find((rpoId) => rpoIdToRpogId.get(rpoId) === rpogId);
          const prodRpoForRpog = productCombo.options.find((rpoId) => rpoIdToRpogId.get(rpoId) === rpogId);
          // If product combo doesn't have an RPO for this RPOG, we can assume it applies to all, so it's a match
          // Otherwise, check if the RPOs from the product and package combos match
          return !prodRpoForRpog || prodRpoForRpog === pkgRpoForRpog;
        });
      });
    })
  );
}

/** Initialize material variants for a new personalization option */
function initializeOptions(
  personalizationType: ReadyPlanOptionGroupPersonalizationType,
  formState: ObjectState<FormValue>,
  tli: EditPersonalizationOptionPage_Slot_TakeoffLineItemFragment,
) {
  // Group products by material variant to handle duplicated cards
  const groupedProducts = tli.products
    .filter((p) => !!p.materialVariant && p.active)
    .groupBy((product) => product.materialVariant!.id);
  // Update product/package options based on the personalization type
  if (
    personalizationType === ReadyPlanOptionGroupPersonalizationType.SingleProduct ||
    personalizationType === ReadyPlanOptionGroupPersonalizationType.LinkedProduct
  ) {
    // We've already initialized, and the useEffect ran again
    if (formState.productOptions.value?.length > 0) return;
    // productOptions only need `materialVariantId` & `combos` set...
    formState.productOptions.set(
      groupedProducts.toEntries().map(([materialVariantId, products]) => ({
        materialVariantId,
        data: fragment(products.first!.materialVariant!),
        // Use all target combinations for the material variant
        personalizationTargetCombinations: rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations)),
      })),
    );
  } else if (personalizationType === ReadyPlanOptionGroupPersonalizationType.Package) {
    // We've already initialized, and the useEffect ran again
    if (formState.packageOptions.value?.length > 0) return;
    // packageOptions need the "column" `placeholderMaterialId` + cells of `productMaterial`s
    // Because this is the 1st placeholder, we create 1 row for each existing card (i.e. two refrigerators become
    // a GE Appliance row & Samsung Appliance row, although the user must still add the option names)
    formState.packageOptions.set(
      groupedProducts.toEntries().map(([_, products]) => ({
        products: [
          {
            placeholderMaterialId: tli.materialVariant!.id,
            productMaterial: { id: products.first!.materialVariant!.id, data: products.first!.materialVariant! },
            // This is a good default for groupByFeature; but if groupByFeature is disabled, it ends up being fine anyway
            // b/c a) the FE code will flip to "unique by placeholderMaterialId", and b) the BE will ignore our locationId
            locationId: tli.location.id,
          },
        ],
        personalizationTargetCombinations: rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations)),
      })),
    );
  } else {
    assertNever(personalizationType);
  }
}

/** Initialize material variants when adding a new slot to a personalization option */
function addExistingMaterials(
  formState: ObjectState<FormValue>,
  placeholder: EditPersonalizationOptionPage_Slot_TakeoffLineItemFragment,
  rpoIdToRpogId: Map<string, string>,
) {
  const personalizationType = formState.type.value!;
  if (
    personalizationType === ReadyPlanOptionGroupPersonalizationType.SingleProduct ||
    personalizationType === ReadyPlanOptionGroupPersonalizationType.LinkedProduct
  ) {
    // For Single/Linked options, we should keep new product cards and add them to the material variant pool
    placeholder.products
      .filter((tli) => !!tli.materialVariant && tli.active)
      .filter((tli) => !formState.productOptions.value.some((po) => po.materialVariantId === tli.materialVariant!.id))
      .forEach((tli) => {
        formState.productOptions.add({
          materialVariantId: tli.materialVariant!.id,
          data: fragment(tli.materialVariant!),
          personalizationTargetCombinations: [],
        });
      });
  } else if (personalizationType === ReadyPlanOptionGroupPersonalizationType.Package) {
    // For package options, set material variants for the new placeholder when target combinations match specific option sets
    // Group products by material variant to handle duplicated cards
    placeholder.products
      .filter((p) => !!p.materialVariant && p.active)
      .groupBy((product) => product.materialVariant!.id)
      .toEntries()
      .forEach(([materialVariantId, products]) => {
        // Merge all target combinations from products sharing the same material variant
        const targetCombinations = rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations));
        // Get all matching package options using partial combination matching
        const matchingPackageOptions = formState.packageOptions.rows.filter((po) =>
          hasMatchingPartialCombination(targetCombinations, po.personalizationTargetCombinations.value, rpoIdToRpogId),
        );
        // Add material variants to each matching package option
        matchingPackageOptions.forEach((matchingPackageOption) => {
          // If the package option already has a product assigned to this cell, don't add a 2nd/duplicate product
          if (
            !matchingPackageOption.products.value.some(
              (p) =>
                p.placeholderMaterialId === placeholder.materialVariant!.id &&
                (!formState.groupByFeature.value || p.locationId === placeholder.location.id),
            )
          ) {
            matchingPackageOption.products.add({
              placeholderMaterialId: placeholder.materialVariant!.id,
              productMaterial: { id: materialVariantId, data: products.first!.materialVariant! },
              locationId: placeholder.location.id,
            });
          }
        });
      });
  } else {
    assertNever(personalizationType);
  }
}

function maybeFilterByPlaceholder(form: ObjectState<FormValue>) {
  const type = form.type.value;
  if (!type) return;
  if (
    type === ReadyPlanOptionGroupPersonalizationType.LinkedProduct ||
    type === ReadyPlanOptionGroupPersonalizationType.SingleProduct
  ) {
    return form.placeholders.rows.first?.data.value.materialVariant?.id;
  } else if (type === ReadyPlanOptionGroupPersonalizationType.Package) {
    return undefined;
  } else {
    assertNever(type);
  }
}

/** Toggles package options between groupByFeature on/off. */
function maybeGroupByFeature(
  formState: ObjectState<FormValue>,
  groupByFeature: boolean,
  rpoIdToRpogId: Map<string, string>,
) {
  // If group by feature is enabled, there should be a product for every location + material variant in all package options
  if (groupByFeature) {
    // Set appropriate locations for existing products (i.e., use the first controlled placeholder's location per material variant)
    const locationIdByPlaceholderMvId = formState.placeholders.rows
      .uniqueBy((p) => p.data.value.materialVariant!.id)
      .keyBy(
        (p) => p.data.value.materialVariant!.id,
        (p) => p.data.value.location.id,
      );
    formState.packageOptions.rows
      .flatMap((po) => po.products.rows)
      .forEach((p) => p.locationId.set(locationIdByPlaceholderMvId[p.placeholderMaterialId.value]));
    const assignedColumns = new Set(
      formState.packageOptions.value.flatMap((po) => po.products).map((p) => p.locationId + p.placeholderMaterialId),
    );
    const emptyPlaceholders = formState.placeholders.rows.filter((placeholder) => {
      const { location, materialVariant } = placeholder.data.value;
      return !assignedColumns.has(location.id + materialVariant?.id);
    });
    // Add material variants for missing placeholders
    emptyPlaceholders.forEach((placeholder) => addExistingMaterials(formState, placeholder.data.value, rpoIdToRpogId));
  } else {
    // If not enabled, ensure there's just one product for every material variant
    formState.packageOptions.rows.forEach((po) => {
      // We cannot use our placeholder.locations (i.e. `Bath / Shower` or `Bath`, to use that for "picking a winner",
      // which is what we originally tried here) because when `groupByFeature` is off, the backend will nudge our
      // productTlis to match the `RPOG.location`, which for a cross-room option might be `Building`.
      //
      // So instead just do a `groupBy(placeholder)` and pick a random/first winner.
      po.products.value
        .groupByObject((po) => po.placeholderMaterialId)
        .filter(([, matches]) => matches.length > 1)
        .forEach(([, matches]) => {
          const [, ...losers] = matches;
          losers.forEach((loser) => po.products.remove(loser));
        });
      // Ensure all product locations are unset because they're not used when group by feature is off.
      // We want to make sure we don't unnecessarily introduce changed values when toggling group by feature on and off
      // (i.e., the form state is initialized with `locationId: undefined` when group by feature is off).
      po.products.rows.forEach((p) => p.locationId.set(undefined));
    });
  }
}

/** Determines whether target group/combinations can be assigned given the personalization type */
function canAssignTargetCombinations(
  personalizationType: InputMaybe<ReadyPlanOptionGroupPersonalizationType>,
): boolean {
  // Hide target groups section for single product options, since target combinations should be defined
  // at the granular level of the placeholder cards, differently from Linked Product and Package options that default combos accross all slots.
  return personalizationType !== ReadyPlanOptionGroupPersonalizationType.SingleProduct;
}
